import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Heart, HandHeart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import api, { formatApiError } from "@/lib/api";

const COUNTRIES = ["Portugal", "Espanha", "França", "Itália", "Alemanha", "Bélgica", "Suíça", "Luxemburgo"];

function MembershipForm({ type }) {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [communities, setCommunities] = useState([]);
  const [form, setForm] = useState({
    type, name: "", email: "", phone: "", country: "", city: "",
    community_id: params.get("community") || "", parish: "", message: "", consent_rgpd: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get("/communities").then((r) => setCommunities(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_rgpd) { toast.error(t("form_consent")); return; }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.community_id) delete payload.community_id;
      await api.post("/membership", payload);
      toast.success(t("join_success"));
      setDone(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (done) return <div className="bg-green-50 text-green-700 rounded-xl p-6 text-center" data-testid={`${type}-success`}>{t("join_success")}</div>;

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`${type}-form`}>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label>{t("form_name")}</Label><Input required value={form.name} data-testid={`${type}-name`} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
        <div><Label>{t("form_email")}</Label><Input required type="email" value={form.email} data-testid={`${type}-email`} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
        <div><Label>{t("form_phone")}</Label><Input value={form.phone} data-testid={`${type}-phone`} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
        <div>
          <Label>{t("form_country")}</Label>
          <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
            <SelectTrigger className="mt-1" data-testid={`${type}-country`}><SelectValue placeholder={t("form_country")} /></SelectTrigger>
            <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>{t("form_city")}</Label><Input value={form.city} data-testid={`${type}-city`} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1" /></div>
        {type === "join" ? (
          <div>
            <Label>{t("form_community")}</Label>
            <Select value={form.community_id} onValueChange={(v) => setForm({ ...form, community_id: v })}>
              <SelectTrigger className="mt-1" data-testid="join-community"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{communities.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ) : (
          <div><Label>{t("parish")}</Label><Input value={form.parish} data-testid="create-parish" onChange={(e) => setForm({ ...form, parish: e.target.value })} className="mt-1" /></div>
        )}
      </div>
      <div><Label>{t("form_message")}</Label><Textarea value={form.message} data-testid={`${type}-message`} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1" rows={3} /></div>
      <label className="flex items-start gap-3 cursor-pointer">
        <Checkbox checked={form.consent_rgpd} data-testid={`${type}-consent`} onCheckedChange={(v) => setForm({ ...form, consent_rgpd: !!v })} className="mt-0.5" />
        <span className="text-sm text-slate-600">{t("form_consent")}</span>
      </label>
      <Button type="submit" disabled={submitting} data-testid={`${type}-submit`} className="w-full bg-th-blue text-white hover:bg-th-bluelight h-11">{submitting ? t("submitting") : t("submit")}</Button>
    </form>
  );
}

export default function Join() {
  const { t } = useI18n();
  return (
    <div>
      <section className="bg-th-blue th-pattern text-white py-14">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <HandHeart className="w-10 h-10 text-th-gold mx-auto mb-3" />
          <h1 className="font-display font-bold text-4xl">{t("nav_join")}</h1>
        </div>
      </section>
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Tabs defaultValue="join">
          <TabsList className="grid grid-cols-2 w-full mb-6">
            <TabsTrigger value="join" data-testid="tab-join">{t("join_title")}</TabsTrigger>
            <TabsTrigger value="create" data-testid="tab-create">{t("create_comm_title")}</TabsTrigger>
          </TabsList>
          <TabsContent value="join"><MembershipForm type="join" /></TabsContent>
          <TabsContent value="create"><MembershipForm type="create_community" /></TabsContent>
        </Tabs>
      </section>
    </div>
  );
}
